import { useState } from 'react';
import { ShoppingListItem as ShoppingListItemType } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Check, 
  MoreVertical, 
  Edit2, 
  Trash2,
  Info
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface ShoppingListItemProps {
  item: ShoppingListItemType;
  isChecked?: boolean;
  onToggleChecked?: (ingredient: string, checked: boolean) => void;
  onEdit?: (item: ShoppingListItemType) => void;
  onRemove?: (item: ShoppingListItemType) => void;
  showRecipes?: boolean;
  isCustomItem?: boolean;
  className?: string;
}

export function ShoppingListItem({
  item,
  isChecked = false,
  onToggleChecked,
  onEdit,
  onRemove,
  showRecipes = true,
  isCustomItem = false,
  className
}: ShoppingListItemProps) {
  const [showDetails, setShowDetails] = useState(false);

  const handleToggleChecked = () => {
    onToggleChecked?.(item.ingredient, !isChecked);
  };

  const handleEdit = () => {
    onEdit?.(item);
  };

  const handleRemove = () => {
    onRemove?.(item);
  };

  const quantityText = item.totalQuantity 
    ? `${item.totalQuantity}${item.unit ? ` ${item.unit}` : ''}`
    : item.unit || '';

  return (
    <div 
      className={cn(
        "group relative bg-white border rounded-lg p-3 transition-all duration-200",
        "hover:shadow-md hover:border-gray-300",
        isChecked && "bg-gray-50 border-gray-200",
        className
      )}
    >
      {/* Main Content */}
      <div className="flex items-center gap-3">
        {/* Checkbox */}
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-6 w-6 p-0 border-2 rounded",
            isChecked 
              ? "bg-green-500 border-green-500 text-white hover:bg-green-600" 
              : "border-gray-300 hover:border-gray-400"
          )}
          onClick={handleToggleChecked}
        >
          {isChecked && <Check className="h-3 w-3" />}
        </Button>

        {/* Item Info */}
        <div className="flex-1 min-w-0">
          {/* Item Name */}
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium text-gray-900 capitalize",
              isChecked && "line-through text-gray-500"
            )}>
              {item.ingredient}
            </span>
            
            {/* Custom Item Badge */}
            {isCustomItem && (
              <Badge variant="outline" className="text-xs h-auto px-1 py-0">
                Custom
              </Badge>
            )}
          </div>

          {/* Quantity */}
          {quantityText && (
            <div className={cn(
              "text-sm text-gray-600",
              isChecked && "line-through text-gray-400"
            )}>
              {quantityText}
            </div>
          )}

          {/* Recipes that use this ingredient */}
          {showRecipes && item.recipes && item.recipes.length > 0 && (
            <div className="mt-1">
              {item.recipes.length === 1 ? (
                <div className="text-xs text-gray-500">
                  Para: {item.recipes[0]}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">
                    Para {item.recipes.length} recetas
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    <Info className="h-3 w-3" />
                  </Button>
                </div>
              )}
              
              {/* Expanded recipe list */}
              {showDetails && item.recipes.length > 1 && (
                <div className="mt-2 pl-2 border-l-2 border-gray-100">
                  {item.recipes.map((recipeName, index) => (
                    <div key={index} className="text-xs text-gray-500 py-0.5">
                      • {recipeName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quantity Display */}
        {quantityText && (
          <div className="text-right">
            <div className={cn(
              "text-sm font-medium text-gray-700",
              isChecked && "line-through text-gray-400"
            )}>
              {quantityText}
            </div>
          </div>
        )}

        {/* Actions Menu */}
        {(onEdit || onRemove) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              )}
              {onRemove && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleRemove}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Aisle Indicator */}
      {item.aisle && (
        <div className="absolute -top-1 -left-1">
          <div 
            className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              getAisleColor(item.aisle)
            )}
          >
            {item.aisle}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to get aisle colors
function getAisleColor(aisle: string): string {
  const aisleColors: Record<string, string> = {
    'verduras': 'bg-green-100 text-green-800',
    'frutas': 'bg-orange-100 text-orange-800', 
    'carnes': 'bg-red-100 text-red-800',
    'pescados': 'bg-blue-100 text-blue-800',
    'lácteos': 'bg-yellow-100 text-yellow-800',
    'despensa': 'bg-amber-100 text-amber-800',
    'especias': 'bg-purple-100 text-purple-800',
    'otros': 'bg-gray-100 text-gray-800',
  };
  
  return aisleColors[aisle.toLowerCase()] || aisleColors['otros'];
}
